import {Injectable} from '@angular/core';
import {ApiClientService} from '../api-client/api-client.service';
import {BehaviorSubject, Observable, Subject, Subscriber} from 'rxjs';
import {ApiResponse} from '../../api/api-response';
import {Report} from '../../models/statistic/report';
import {ConversionReport} from '../../models/statistic/conversion-report';
import {ClickReport} from '../../models/statistic/click-report';
import {AbstractModel} from '../../models/abstract-model';
import {Paginator} from '../../tools/pagination/paginator';
import {GlobalHomeStat} from '../../models/statistic/global-home-stat';
import {TopOffer} from '../../models/statistic/top-offer';
import {Offer} from '../../models/offer';

export interface ExportProgress {
  current: number;
  total: number;
}

@Injectable({
  providedIn: 'root'
})
export class StatisticService {

  static readonly REPORTS: string[] = ['daily', 'subid', 'offer'];

  $filters: BehaviorSubject<any> = new BehaviorSubject<any>({});

  // Emitted by the shared report filter bar, consumed by the active report tab
  $exportCsv: Subject<void> = new Subject<void>();

  // Published by the active report tab while a multi-request export runs, so the
  // filter bar can show progress and block a second click
  $exportProgress: BehaviorSubject<ExportProgress | null> = new BehaviorSubject<ExportProgress | null>(null);

  constructor(private apiClient: ApiClientService) {
  }

  isExporting(): boolean {
    return this.$exportProgress.getValue() !== null;
  }

  fetchBasicReport(report: string, filters: any): Observable<Report[]> {
    return this.fetchReport(report, filters, null, Report);
  }

  fetchConversionReport(filters: any, paginator: Paginator | null = null): Observable<ConversionReport[]> {
    return this.fetchReport('conversion', filters, paginator, ConversionReport);
  }

  fetchClickReport(filters: any, paginator: Paginator | null = null): Observable<ClickReport[]> {
    return this.fetchReport('click', filters, paginator, ClickReport);
  }

  fetchGlobalHome(): Observable<GlobalHomeStat> {
    return new Observable<GlobalHomeStat>((subscriber: Subscriber<GlobalHomeStat>) => {
      this.apiClient.get('/statistics/home/global').subscribe((response: ApiResponse) => {
        subscriber.next(new GlobalHomeStat().deserialize(response.getData()));
        subscriber.complete();
      });
    })
  }

  fetchTopOffers(filters: any): Observable<TopOffer[]> {
    return this.fetchReport('home/top-offers', filters, null, TopOffer);
  }

  fetchActiveOffers(filters: any): Observable<Offer[]> {
    return new Observable<Offer[]>((subscriber: Subscriber<Offer[]>) => {
      this.apiClient.get('/statistics/active-offers', filters).subscribe((response: ApiResponse) => {
        subscriber.next((response.getData() ?? []).map((offer: any) => new Offer().deserialize(offer)));
        subscriber.complete();
      });
    })
  }

  fetchUsedOfferIds(): Observable<number[]> {
    return new Observable<number[]>((subscriber: Subscriber<number[]>) => {
      this.apiClient.get('/statistics/used-offers').subscribe((response: ApiResponse) => {
        subscriber.next(response.getData() ?? []);
        subscriber.complete();
      });
    })
  }

  private fetchReport<TModel extends AbstractModel>(
    report: string,
    filters: any,
    paginator: Paginator | null = null,
    _createClass: { new(): TModel }
  ): Observable<TModel[]> {
    return new Observable<TModel[]>((subscriber: Subscriber<TModel[]>) => {
      if (report !== 'home/top-offers' && (!filters || !filters.startDate && !filters.endDate)) {
        subscriber.next([]);
        subscriber.complete();
        return;
      }

      if (paginator) {
        filters.page = paginator.currentPage;
        filters.count = paginator.countByPage;
      }

      this.apiClient.get('/statistics/' + report, filters).subscribe((response: ApiResponse) => {
        if (StatisticService.REPORTS.includes(report)) {
          const rows = response.getData().rows.map((statistic: any) => new _createClass().deserialize(statistic));
          rows.push(new _createClass().deserialize(response.getData().totals));

          subscriber.next(rows);
          subscriber.complete();
        } else {
          if (paginator) {
            paginator.count = response.getTotal();
          }

          subscriber.next(response.getData().map((statistic: any) => new _createClass().deserialize(statistic)));
          subscriber.complete();
        }
      });
    });
  }

}
